import { http } from '@noor/api';
import { env } from '@noor/config';
import {
  merchantListSchema,
  merchantSchema,
  employeeListSchema,
  employeeSchema,
  merchantIntegrationSchema,
  integrationOptionsSchema,
  merchantIntegrationCredentialsSchema,
  pharmaIntegrationSchema,
  integrationTokenListSchema,
  generateIntegrationTokenResponseSchema,
  type Merchant,
  type MerchantList,
  type Employee,
  type EmployeeList,
  type CreateMerchantPayload,
  type UpdateMerchantPayload,
  type AttachEmployeePayload,
  type UpdateEmployeePayload,
  type MerchantIntegration,
  type IntegrationOptions,
  type UpdateMerchantIntegrationPayload,
  type DeliveryPricing,
  type UpdateDeliveryPricingPayload,
  deliveryPricingSchema,
  type MerchantIntegrationCredentialsPayload,
  type PharmaIntegration,
  type UpdatePharmaIntegrationPayload,
  type IntegrationTokenList,
  type GenerateIntegrationTokenResponse,
} from '../model/schema';
import {
  toMerchant,
  toMerchantList,
  buildMerchantListQuery,
  buildIntegrationTokenListQuery,
  type MerchantListParams,
  type IntegrationTokenListParams,
} from '../lib/merchant-mappers';
import {
  toMerchantArchiveEndpoint,
  type MerchantArchiveAction,
} from '../lib/merchant-status';
import { deliveryVendorSchema, type DeliveryVendor } from '../lib/delivery-sync';

/**
 * Merchant-profile admin API — the EXACT hosts + paths cpanel used
 * (`features/merchants/api/*`, clients `utils/axios/index.ts`):
 *
 * - merchant brands, staff, logo presign, and the merchants filter
 *   list → Core host (`VITE_CORE_API_URL`, old `eatsRequest`), paths carry
 *   `/api/v1`;
 * - eats merchant detail + integration writes → Eats host
 *   (`VITE_EATS_API_URL`, `openapi-eats-admin.json`), paths carry `/api/v1`;
 * - pharma company integration → Pharma host (`VITE_PHARMA_API_URL`, old
 *   `pharmaRequest`), paths carry `/api/v1`;
 * - vendor token issuance + delivery-vendor mirror → main gateway
 *   (`VITE_API_BASE_URL`, old `requestMain`), paths relative (the base
 *   already carries `/api/v1`).
 *
 * Every call goes through the shared `http` client (Bearer [REDACTED] locale +
 * silent refresh) via a per-request `baseURL` override, and parses at the
 * boundary. Query objects drop `undefined` (the old `qs` `skipNulls`).
 */

const coreBase = { baseURL: env.VITE_CORE_API_URL };
const eatsBase = { baseURL: env.VITE_EATS_API_URL };
const pharmaBase = { baseURL: env.VITE_PHARMA_API_URL };
const MERCHANT_LIST_TIMEOUT_MS = 10_000;

function timedSignal(signal?: AbortSignal): {
  signal: AbortSignal;
  cleanup: () => void;
} {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), MERCHANT_LIST_TIMEOUT_MS);
  const abort = () => controller.abort(signal?.reason);
  signal?.addEventListener('abort', abort, { once: true });

  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', abort);
    },
  };
}

interface PresignUploadItem {
  key?: string;
  upload_url?: string;
  public_url?: string;
  error?: string;
}

export const MERCHANT_PROFILE_UPLOAD_BUCKET = 'merchant-profile';

function isRecordLike(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Unwrap `{data:{integration}}` / `{data}` / flat pharma payloads. */
function unwrapIntegrationEnvelope(raw: unknown): unknown {
  if (!isRecordLike(raw)) return raw;
  const data: unknown = raw.data;
  if (isRecordLike(data) && 'integration' in data) return data.integration;
  return data ?? raw;
}

export const merchantApi = {
  /**
   * Filter list — `GET /api/v1/admin/merchants/filter` with
   * `search_type` / `search_value` / `limit` / `offset`. Failures arrive as
   * the `{error:{code,message}}` envelope, normalized by the shared http
   * client into `ApiError`.
   */
  async list(
    params: MerchantListParams,
    options?: { signal?: AbortSignal },
  ): Promise<MerchantList> {
    const request = timedSignal(options?.signal);
    try {
      const raw = await http.get<unknown>('/api/v1/admin/merchants/filter', {
        ...coreBase,
        signal: request.signal,
        query: buildMerchantListQuery(params),
      });
      return merchantListSchema.parse(toMerchantList(raw));
    } finally {
      request.cleanup();
    }
  },

  async detail(merchantId: string): Promise<Merchant | undefined> {
    const raw = await http.get<unknown>(
      `/api/v1/admin/merchants/${merchantId}`,
      coreBase,
    );
    return toMerchant(raw);
  },

  async create(payload: CreateMerchantPayload): Promise<Merchant | undefined> {
    const raw = await http.post<unknown>('/api/v1/admin/merchants', payload, coreBase);
    return toMerchant(raw);
  },

  async update(
    merchantId: string,
    payload: UpdateMerchantPayload,
  ): Promise<Merchant | undefined> {
    const raw = await http.put<unknown>(
      `/api/v1/admin/merchants/${merchantId}`,
      payload,
      coreBase,
    );
    return toMerchant(raw);
  },

  async remove(merchantId: string): Promise<void> {
    await http.delete<unknown>(`/api/v1/admin/merchants/${merchantId}`, coreBase);
  },

  /**
   * Archive a merchant — `POST /api/v1/admin/merchants/:id/archive` on the
   * core merchant-profile host. The response is the split `data` + `list`
   * detail envelope; failures arrive as the `{error:{code,message}}`
   * envelope, normalized by the shared http client into `ApiError`.
   */
  async archive(merchantId: string): Promise<Merchant | undefined> {
    const raw = await http.post<unknown>(
      `/api/v1/admin/merchants/${encodeURIComponent(merchantId)}/archive`,
      {},
      coreBase,
    );
    return toMerchant(raw);
  },

  /**
   * Restore a merchant from the archive —
   * `POST /api/v1/admin/merchants/:id/unarchive` on the core
   * merchant-profile host. Same envelope contract as `archive`.
   */
  async unarchive(merchantId: string): Promise<Merchant | undefined> {
    const raw = await http.post<unknown>(
      `/api/v1/admin/merchants/${encodeURIComponent(merchantId)}/unarchive`,
      {},
      coreBase,
    );
    return toMerchant(raw);
  },

  /**
   * Archive / restore a merchant — the `clients/all` row action, moved over
   * without its role gate. Dispatches to the dedicated `archive` /
   * `unarchive` endpoints above (`activate` is the legacy restore action
   * name and hits `unarchive`).
   */
  async updateStatus(
    merchantId: string,
    action: MerchantArchiveAction,
  ): Promise<Merchant | undefined> {
    const endpoint = toMerchantArchiveEndpoint(action);
    return endpoint === 'archive'
      ? merchantApi.archive(merchantId)
      : merchantApi.unarchive(merchantId);
  },

  /**
   * Verticals are a dedicated PUT (PATCH does not accept them). Dropping the
   * pharma vertical also deletes the pharma-company integration — kept 1:1.
   */
  async setVerticals(args: {
    merchantId: string;
    previousVerticals?: string[] | undefined;
    verticals: string[];
  }): Promise<Merchant | undefined> {
    const raw = await http.put<unknown>(
      `/api/v1/admin/merchants/${args.merchantId}/verticals`,
      {
        verticals: args.verticals,
      },
      coreBase,
    );
    const removedPharma =
      args.previousVerticals?.includes('MERCHANT_VERTICAL_PHARMA') &&
      !args.verticals.includes('MERCHANT_VERTICAL_PHARMA');
    if (removedPharma) {
      await http
        .delete<unknown>(`/api/v1/companies/${args.merchantId}/integration`, pharmaBase)
        .catch(() => undefined);
    }
    return toMerchant(raw);
  },

  /* ── Employees (Core host, Merchant Employee Admin Service) ── */

  async employees(params: {
    merchant_id: string;
    limit?: number;
    offset?: number;
  }): Promise<EmployeeList> {
    const raw = await http.get<unknown>('/api/v1/admin/merchant-employees', {
      ...coreBase,
      query: {
        ...(params.merchant_id ? { merchant_id: params.merchant_id } : {}),
        ...(params.limit !== undefined ? { limit: params.limit } : {}),
        ...(params.offset !== undefined ? { offset: params.offset } : {}),
      },
    });
    const parsed = employeeListSchema.safeParse(raw);
    if (parsed.success) return parsed.data;
    const list = isRecordLike(raw) && Array.isArray(raw.list) ? raw.list : [];
    return {
      list: (list as unknown[])
        .map((e) => employeeSchema.safeParse(e).data)
        .filter((e): e is Employee => Boolean(e)),
    };
  },

  async employee(employeeId: string): Promise<Employee | undefined> {
    const raw = await http.get<unknown>(
      `/api/v1/admin/merchant-employees/${encodeURIComponent(employeeId)}`,
      coreBase,
    );
    const body = isRecordLike(raw) ? raw.data : undefined;
    const parsed = employeeSchema.safeParse(body);
    return parsed.success ? parsed.data : undefined;
  },

  /**
   * Attach an employee — `POST /api/v1/admin/merchant-employees` with either
   * `{merchant_id, employee_id}` or `{merchant_id, new_identity:{phone, role,
   * first_name?, last_name?}}`. Answers the `{data:{employee}, meta}`
   * envelope; a repeated attach returns the existing row (idempotent).
   */
  async attachEmployee(payload: AttachEmployeePayload): Promise<Employee | undefined> {
    const raw = await http.post<unknown>(
      '/api/v1/admin/merchant-employees',
      payload,
      coreBase,
    );
    const body = isRecordLike(raw) ? raw.data : undefined;
    const employee = isRecordLike(body) ? body.employee : undefined;
    const parsed = employeeSchema.safeParse(employee);
    return parsed.success ? parsed.data : undefined;
  },

  /**
   * Update an employee — `PATCH
   * /api/v1/admin/merchant-employees/{employee_id}` with
   * `{first_name?, last_name?, role?}`. Phone is not accepted by this API.
   * Answers `{data: employee, meta}`; an empty role
   * means "don't touch the role".
   */
  async updateEmployee(
    employeeId: string,
    payload: UpdateEmployeePayload,
  ): Promise<Employee | undefined> {
    const raw = await http.patch<unknown>(
      `/api/v1/admin/merchant-employees/${encodeURIComponent(employeeId)}`,
      payload,
      coreBase,
    );
    const body = isRecordLike(raw) ? raw.data : undefined;
    const parsed = employeeSchema.safeParse(body ?? raw);
    return parsed.success ? parsed.data : undefined;
  },

  /**
   * Detach an employee — `DELETE
   * /api/v1/admin/merchant-employees/{employee_id}`.
   */
  async detachEmployee(employeeId: string): Promise<void> {
    await http.delete<unknown>(
      `/api/v1/admin/merchant-employees/${encodeURIComponent(employeeId)}`,
      coreBase,
    );
  },

  /* ── Eats integration (Eats host, `openapi-eats-admin.json`) ── */

  /**
   * Eats integration settings — `GET /api/v1/admin/catalog-merchants/{id}` on the Eats
   * host (`VITE_EATS_API_URL`). Answers `{data:{merchant:{id, name,
   * status, created_at, updated_at, menu_source, delivery_provider}},
   * meta:{timestamp}}`; `menu_source` / `delivery_provider` are never empty
   * (NOT NULL with `internal` / `noor` defaults). `X-Platform: web` rides
   * along via the shared http client.
   */
  async integration(merchantId: string): Promise<MerchantIntegration | undefined> {
    const raw = await http.get<unknown>(
      `/api/v1/admin/catalog-merchants/${encodeURIComponent(merchantId)}`,
      eatsBase,
    );
    const body = isRecordLike(raw) ? raw.data : undefined;
    const merchant = isRecordLike(body) ? body.merchant : undefined;
    const parsed = merchantSchema.safeParse(merchant ?? body);
    if (!parsed.success) return undefined;
    const menuSource = parsed.data.menu_source;
    const deliveryProvider = parsed.data.delivery_provider;
    return merchantIntegrationSchema.parse({
      merchant_id: parsed.data.id,
      menu_source:
        menuSource === undefined || menuSource === '' ? 'internal' : menuSource,
      delivery_provider:
        deliveryProvider === undefined || deliveryProvider === ''
          ? 'noor'
          : deliveryProvider,
      send_orders_to_noor: parsed.data.send_orders_to_noor,
      // protobuf-JSON omits a zero scalar, so an absent rate IS 0 (non-payer).
      vat_percent: parsed.data.vat_percent ?? 0,
      commission_percent: parsed.data.commission_percent ?? 0,
    });
  },

  /**
   * Brand VAT rate — `PATCH /api/v1/admin/catalog-merchants/{id}` with
   * `{vat_percent}`. Admin-only. The field is never nullable, so there is no
   * "reset": `0` means the merchant is not a VAT payer.
   */
  async updateVatPercent(merchantId: string, vatPercent: number): Promise<void> {
    await http.patch<unknown>(
      `/api/v1/admin/catalog-merchants/${encodeURIComponent(merchantId)}`,
      { vat_percent: vatPercent },
      eatsBase,
    );
  },

  /** Noor commission — admin-only PATCH /api/v1/admin/catalog-merchants/{id}. */
  async updateCommissionPercent(
    merchantId: string,
    commissionPercent: number,
  ): Promise<void> {
    await http.patch<unknown>(
      `/api/v1/admin/catalog-merchants/${encodeURIComponent(merchantId)}`,
      { commission_percent: commissionPercent },
      eatsBase,
    );
  },

  /**
   * Select options for the Eats tab — `GET /api/v1/admin/catalog-merchants/integration-options`
   * on the Eats host
   * (`VITE_EATS_API_URL`). Answers `{data: {menu_sources, delivery_providers}}`.
   */
  async integrationOptions(): Promise<IntegrationOptions> {
    const raw = await http.get<unknown>(
      '/api/v1/admin/catalog-merchants/integration-options',
      eatsBase,
    );
    const body = isRecordLike(raw) ? raw.data : undefined;
    return integrationOptionsSchema.parse(body ?? raw);
  },

  async integrationCredentials(merchantId: string, provider: string) {
    const raw = await http.get<unknown>(
      `/api/v1/admin/catalog-merchants/${encodeURIComponent(merchantId)}/integrations/${encodeURIComponent(provider)}/credentials`,
      eatsBase,
    );
    const body = isRecordLike(raw) && isRecordLike(raw.data) ? raw.data : raw;
    return merchantIntegrationCredentialsSchema.parse(body);
  },

  async updateIntegration(
    merchantId: string,
    payload: UpdateMerchantIntegrationPayload,
  ): Promise<MerchantIntegration | undefined> {
    const raw = await http.patch<unknown>(
      `/api/v1/admin/catalog-merchants/${encodeURIComponent(merchantId)}/integration`,
      payload,
      eatsBase,
    );
    const body = isRecordLike(raw) ? raw.data : undefined;
    const parsed = merchantIntegrationSchema.safeParse(body ?? raw);
    return parsed.success ? parsed.data : undefined;
  },

  async updateIntegrationCredentials(
    merchantId: string,
    provider: string,
    credentials: MerchantIntegrationCredentialsPayload,
  ): Promise<unknown> {
    const raw = await http.put<unknown>(
      `/api/v1/admin/catalog-merchants/${encodeURIComponent(merchantId)}/integrations/${provider}/credentials`,
      credentials,
      eatsBase,
    );
    return isRecordLike(raw) && 'data' in raw ? raw.data : raw;
  },

  async deliveryPricing(merchantId: string): Promise<DeliveryPricing> {
    const raw = await http.get<unknown>(
      `/api/v1/admin/catalog-merchants/${encodeURIComponent(merchantId)}/delivery-pricing`,
      eatsBase,
    );
    // GET returns `{ delivery_pricing, delivery_payment }`; the PATCH response
    // instead uses `{ pricing, payment }`. The schema accepts both response
    // spellings, while the write payload follows the OpenAPI request contract.
    const body = isRecordLike(raw) && 'data' in raw ? raw.data : raw;
    return deliveryPricingSchema.parse(body);
  },

  async updateDeliveryPricing(merchantId: string, payload: UpdateDeliveryPricingPayload) {
    const raw = await http.patch<unknown>(
      `/api/v1/admin/catalog-merchants/${encodeURIComponent(merchantId)}/delivery-pricing`,
      payload,
      eatsBase,
    );
    const body = isRecordLike(raw) && 'data' in raw ? raw.data : raw;
    return deliveryPricingSchema.parse(body);
  },

  /* ── Pharma integration (Pharma host) ────────────────────────── */

  async pharmaIntegration(companyId: string): Promise<PharmaIntegration | undefined> {
    const raw = await http.get<unknown>(
      `/api/v1/companies/${companyId}/integration`,
      pharmaBase,
    );
    const parsed = pharmaIntegrationSchema.safeParse(unwrapIntegrationEnvelope(raw));
    return parsed.success ? parsed.data : undefined;
  },

  async updatePharmaIntegration(
    companyId: string,
    payload: UpdatePharmaIntegrationPayload,
  ): Promise<PharmaIntegration | undefined> {
    const raw = await http.put<unknown>(
      `/api/v1/companies/${companyId}/integration`,
      payload,
      pharmaBase,
    );
    const parsed = pharmaIntegrationSchema.safeParse(unwrapIntegrationEnvelope(raw));
    return parsed.success ? parsed.data : undefined;
  },

  /* ── Integration tokens (main gateway, old `requestMain`) ────── */

  async integrationTokens(
    params: IntegrationTokenListParams,
  ): Promise<IntegrationTokenList> {
    const raw = await http.get<unknown>('admin/vendors/integration-tokens', {
      query: buildIntegrationTokenListQuery(params),
    });
    const body = isRecordLike(raw) ? raw.data : undefined;
    return integrationTokenListSchema.parse(body ?? raw);
  },

  async generateIntegrationToken(
    vendorId: number,
  ): Promise<GenerateIntegrationTokenResponse> {
    const raw = await http.post<unknown>(
      `admin/vendors/${vendorId}/integration/generate-token`,
    );
    const body = isRecordLike(raw) ? raw.data : undefined;
    return generateIntegrationTokenResponseSchema.parse(body ?? raw);
  },

  /* ── Delivery vendor mirror (main gateway, old `requestMain`) ── */

  async deliveryVendor(merchantId: string): Promise<DeliveryVendor> {
    const raw = await http.get<unknown>(
      `admin/vendors/by-external-id/${encodeURIComponent(merchantId)}`,
    );
    const body = isRecordLike(raw) ? raw.data : undefined;
    const parsed = deliveryVendorSchema.parse(body ?? raw);
    // The row carries the webhook pair flat (`callback_url`,
    // `callback_token`); a nested `callback: {url, token}` shape fills the
    // gaps when the flat pair is absent.
    return {
      ...parsed,
      callback_url: parsed.callback_url ?? parsed.callback?.url ?? null,
      callback_token: parsed.callback_token ?? parsed.callback?.token ?? null,
    };
  },

  async updateDeliveryVendor(
    vendorId: string | number,
    payload: Record<string, unknown>,
  ): Promise<void> {
    await http.patch<unknown>(`admin/vendors/${vendorId}`, payload);
  },

  /* ── Merchant image upload (Eats presign + S3 PUT, old `upload.endpoints.ts`) ── */

  async uploadLogo(file: File, folder = 'merchants'): Promise<string> {
    const presigned = await http.post<{ list?: PresignUploadItem[] }>(
      '/api/v1/uploads/presign',
      {
        count: 1,
        bucket: MERCHANT_PROFILE_UPLOAD_BUCKET,
        folder,
      },
      coreBase,
    );
    const item = presigned?.list?.[0];
    if (!item?.upload_url)
      throw new Error(item?.error ?? 'Failed to obtain presigned upload URL');

    const put = await fetch(item.upload_url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type.length > 0 ? file.type : 'application/octet-stream',
      },
    });
    if (!put.ok) throw new Error('Failed to upload logo file');

    let publicUrl = '';
    if (item.public_url !== undefined && item.public_url !== '') {
      publicUrl = item.public_url;
    } else if (item.key !== undefined && item.key !== '') {
      publicUrl = `https://s3.test.api.noor.uz/${MERCHANT_PROFILE_UPLOAD_BUCKET}/${item.key}`;
    }
    if (publicUrl === '')
      throw new Error('Failed to obtain public_url for uploaded file');
    return publicUrl;
  },
};
